/*
 * Theme-side lifecycle tracking for the v3 server-session protocol.
 * The synchronous bootstrap owns page capture, consent, the durable Outbox,
 * and the first send. This file adds leave/heartbeat behavior and cross-tab
 * opener tokens without sending the page view a second time.
 */
(function () {
  var bootstrap = window.__SICOTAS_TRACKER_BOOTSTRAP__;
  if (!bootstrap) return;

  var HEARTBEAT_INTERVAL = 30 * 1000;
  var hiddenStart = null;
  var hiddenMs = 0;
  var lifecycleStarted = false;
  var navigationHooksInstalled = false;
  var lastCheckoutIntentAt = 0;

  function currentEvent() {
    return bootstrap.getCurrent ? bootstrap.getCurrent() : null;
  }

  function trackingIdsForEvent(event) {
    var sendState = bootstrap.getSendState ? bootstrap.getSendState() : null;
    if (!event || !event.canonicalSessionId || !event.canonicalPageOrder
        || !sendState || sendState.state !== 'acked') return null;
    return {
      clientId: event.payload && event.payload.clientId,
      sessionId: event.canonicalSessionId,
      pageOrder: event.canonicalPageOrder
    };
  }

  function lifecyclePayload(leaveAt, type) {
    var event = currentEvent();
    if (!event) return null;
    var ids = trackingIdsForEvent(event);
    // A lifecycle event cannot be resolved until its Page View has committed.
    // For very short visits, keep the Page View in the durable Outbox and skip
    // the best-effort lifecycle event instead of creating a guaranteed 5xx.
    if (!ids || !ids.clientId || !ids.sessionId || !ids.pageOrder) return null;
    return {
      schemaVersion: 3,
      eventId: type + ':' + event.pageViewId + ':' + Math.floor(leaveAt / HEARTBEAT_INTERVAL),
      type: type,
      occurredAt: leaveAt,
      clientId: ids.clientId,
      clientSessionId: event.clientSessionId,
      sessionId: ids.sessionId,
      tabId: event.tabId,
      pageViewId: event.pageViewId,
      tabSequence: event.tabSequence,
      pageOrder: ids.pageOrder,
      leaveAt: leaveAt,
      hiddenMs: hiddenMs
    };
  }

  function postLeave(leaveAt, useBeacon) {
    var payload = lifecyclePayload(leaveAt, 'leave');
    if (!payload) return Promise.resolve(false);
    return bootstrap.sendTransient(payload, Boolean(useBeacon));
  }

  function postHeartbeat(heartbeatAt) {
    var payload = lifecyclePayload(heartbeatAt, 'heartbeat');
    if (!payload) return Promise.resolve(false);
    return bootstrap.sendTransient(payload, false);
  }

  function handleVisible() {
    if (!hiddenStart) return;
    var hiddenFor = Date.now() - hiddenStart;
    hiddenMs += hiddenFor;
    hiddenStart = null;
    if (hiddenFor >= bootstrap.sessionTimeout) {
      bootstrap.createResumePageView();
      hiddenMs = 0;
    }
  }

  function cleanNavigationMappings() {
    try {
      var cutoff = Date.now() - 5 * 60 * 1000;
      for (var index = window.localStorage.length - 1; index >= 0; index--) {
        var key = window.localStorage.key(index);
        if (!key || key.indexOf(bootstrap.navPrefix) !== 0) continue;
        var mapping = JSON.parse(window.localStorage.getItem(key) || 'null');
        if (!mapping || Number(mapping.createdAt || 0) < cutoff) window.localStorage.removeItem(key);
      }
    } catch (_) {}
  }

  function newTabIntent(event, anchor) {
    return anchor.target === '_blank' || event.metaKey || event.ctrlKey || event.shiftKey || event.button === 1;
  }

  function decorateNewTabLink(event) {
    var active = currentEvent();
    if (!active || !event || event.defaultPrevented) return;
    var anchor = event.target && event.target.closest ? event.target.closest('a[href]') : null;
    if (!anchor || anchor.hasAttribute('download') || !newTabIntent(event, anchor)) return;
    try {
      var target = new URL(anchor.href, location.href);
      if (target.origin !== location.origin || !/^https?:$/.test(target.protocol)) return;
      if (target.searchParams.has('_trk_nav')) return;
      var navigationId = bootstrap.newId();
      var originalHref = anchor.href;
      window.localStorage.setItem(bootstrap.navPrefix + navigationId, JSON.stringify({
        openerPageViewId: active.pageViewId,
        targetUrl: target.origin + target.pathname + target.search,
        createdAt: Date.now()
      }));
      target.searchParams.set('_trk_nav', navigationId);
      anchor.href = target.href;
      setTimeout(function () {
        if (anchor.href === target.href) anchor.href = originalHref;
      }, 0);
    } catch (_) {}
  }

  function trackCheckoutIntent(source) {
    var now = Date.now();
    if (now - lastCheckoutIntentAt < 1000) return;
    lastCheckoutIntentAt = now;
    var tracker = window.SicotasTracker;
    if (!tracker || typeof tracker.trackCheckoutIntent !== 'function') return;
    tracker.trackCheckoutIntent({ source: source });
  }

  function handleCheckoutClick(event) {
    var target = event.target && event.target.closest
      ? event.target.closest('button[name="checkout"], input[name="checkout"], .cart-slide-drawer__checkout-btn, a[href*="/checkout"]')
      : null;
    if (target) trackCheckoutIntent('checkout_click');
  }

  function handleCheckoutSubmit(event) {
    var submitter = event.submitter;
    var form = event.target;
    var isCheckout = Boolean(submitter && submitter.name === 'checkout');
    if (!isCheckout && form && form.action) {
      try { isCheckout = new URL(form.action, location.href).pathname.indexOf('/checkout') === 0; } catch (_) {}
    }
    if (isCheckout) trackCheckoutIntent('checkout_submit');
  }

  function installNavigationHooks() {
    if (navigationHooksInstalled) return;
    navigationHooksInstalled = true;
    cleanNavigationMappings();
    document.addEventListener('click', decorateNewTabLink, true);
    document.addEventListener('auxclick', decorateNewTabLink, true);
    document.addEventListener('click', handleCheckoutClick, true);
    document.addEventListener('submit', handleCheckoutSubmit, true);
  }

  function startLifecycle() {
    if (lifecycleStarted) return;
    lifecycleStarted = true;
    installNavigationHooks();

    document.addEventListener('visibilitychange', function () {
      if (document.hidden) {
        hiddenStart = Date.now();
        postLeave(hiddenStart, false);
      } else {
        handleVisible();
      }
    });

    window.addEventListener('pagehide', function () {
      var now = Date.now();
      // Give an unacknowledged Page View the first and only unload beacon. A
      // Leave is emitted only when the canonical Page View is already known.
      bootstrap.beaconCurrentPageView();
      postLeave(now, true);
    });

    setInterval(function () {
      if (document.hidden) return;
      var now = Date.now();
      postHeartbeat(now);
      bootstrap.touchLegacyActivity(now);
      bootstrap.flush();
    }, HEARTBEAT_INTERVAL);
  }

  bootstrap.onReady(startLifecycle);
})();
